import clsx from "clsx";
import { Icon } from "../Icon/Icon";
import styles from "./OdorPath.module.css";

/** The 17 ScenTree olfactive families; `n` = ScenTree family id − 100 = the --color-family-<n> swatch. */
export const OLFACTIVE_FAMILIES = [
  { n: 1, name: "Animalic" },
  { n: 2, name: "Musky" },
  { n: 3, name: "Burnt Leather" },
  { n: 4, name: "Undergrowth" },
  { n: 5, name: "Woody" },
  { n: 6, name: "Spicy" },
  { n: 7, name: "Balsamic Ambery" },
  { n: 8, name: "Floral" },
  { n: 9, name: "Herbal" },
  { n: 10, name: "Green" },
  { n: 11, name: "Citrus" },
  { n: 12, name: "Aldehydes" },
  { n: 13, name: "Fruity" },
  { n: 14, name: "Butyric Buttery" },
  { n: 15, name: "Sulfuric" },
  { n: 16, name: "Marine" },
  { n: 17, name: "Solvents" },
] as const;

/** Swatch number for a ScenTree family id (101–117); null for private families without one. */
export function familySwatch(scentreeFamilyId: number | null | undefined): number | null {
  if (scentreeFamilyId === null || scentreeFamilyId === undefined) return null;
  const n = scentreeFamilyId - 100;
  return n >= 1 && n <= 17 ? n : null;
}

export interface OdorTerm {
  id?: string;
  label: string;
  /** Swatch number 1–17 (see familySwatch). Terms without one inherit the path's family. */
  family?: number | null;
}

export interface OdorPathProps {
  /** Family first, then subfamily and descriptors: Floral › Rosy › Rose. */
  terms: readonly OdorTerm[];
  size?: "sm" | "md";
  className?: string;
}

/** An olfactive path as chips tinted with the family swatch; the family name always sits next to the color. */
export function OdorPath({ terms, size = "md", className }: OdorPathProps) {
  const pathFamily = terms.find((term) => term.family)?.family ?? null;
  return (
    <ol className={clsx(styles.path, className)} data-size={size} aria-label="Odor path">
      {terms.map((term, index) => {
        const family = term.family ?? pathFamily;
        return (
          <li key={term.id ?? `${index}-${term.label}`} className={styles.step}>
            {index > 0 ? (
              <span className={styles.separator} aria-hidden="true">
                <Icon name="chevronRight" />
              </span>
            ) : null}
            <span className={styles.chip} data-family={family ?? undefined}>
              {index === 0 ? <span className={styles.dot} aria-hidden="true" /> : null}
              {term.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
